/**
 * Held submissions slice (SCRUM-1951, RTK bundle 2) — the former switch reducer
 * heldSubmissionsReducer.js as createSlice. Behaviour-preserving: every case
 * answers the same legacy action type string (heldSubmissionsTypes.js) that
 * heldSubmissionsActions.ts already dispatches, and returns the same state.
 * The actions module is unchanged, so no importer changes.
 * Characterised by tests/integration/reducers/reviewPublishingReducers.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  GET_HELD_SUBMISSIONS,
  HELD_SUBMISSIONS_LOADING,
  REMOVE_HELD_SUBMISSION,
} from "./heldSubmissionsTypes";
import type { FormSubmissionListItem } from "../../../../shared/apiTypes";

/**
 * The held public form submissions a reviewer sees (SCRUM-1753): the rows of
 * `GET /api/form-submissions?status=held`, `FormSubmissionListItem` in
 * shared/apiTypes.js. A released or discarded row leaves `items`; a refused
 * action (409 / 4xx) leaves it where it is — the error hub shows the reason.
 */
type HeldSubmissionsState = { items: FormSubmissionListItem[]; loading: boolean };

const initialState: HeldSubmissionsState = { items: [], loading: false };

const heldSubmissionsSlice = createSlice({
  name: "heldSubmissions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HELD_SUBMISSIONS_LOADING, (state) => ({ ...state, loading: true }))
      .addCase(GET_HELD_SUBMISSIONS, (state, action: PayloadAction<FormSubmissionListItem[], typeof GET_HELD_SUBMISSIONS>) => ({
        ...state,
        items: action.payload,
        loading: false,
      }))
      .addCase(REMOVE_HELD_SUBMISSION, (state, action: PayloadAction<string, typeof REMOVE_HELD_SUBMISSION>) => ({
        ...state,
        items: state.items.filter((row) => row._id !== action.payload),
      }));
  },
});

export default heldSubmissionsSlice.reducer;
